import { Box, Table, TableHead, TableBody, TableRow, TableCell, Paper, TableContainer, CircularProgress, Pagination, CardHeader, CardContent, Grid, TextField, InputAdornment, IconButton, FormControl, InputLabel, Select, MenuItem, Chip, Stack, Typography } from "@mui/material";
import { contestServices } from "../../services/AllServices";
import { capitalizeFirstLetter } from "../../utils/helpers";
import { formatPrice } from "../../utils/formatNumber";
import React, { useEffect, useState, useCallback } from "react";
import CardLayout from "../../layouts/CardLayout";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import moment from "moment";
import { debounce } from "lodash";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { initializeFromUrlParams, updateSearchParams, updatePageParams } from "../../utils/urlParams";

function FantasyGames() {
  const { id: contestId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchItem, setSearchItem] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [gameTypeFilter, setGameTypeFilter] = useState("");
  const [leagueFilter, setLeagueFilter] = useState("");

  const fetchFantasyGames = async () => {
    setLoading(true);
    try {
      const payload = {
        pageSize,
        pageNumber,
        searchItem: debouncedSearch,
        status: [],
        contestId,
        gameType: gameTypeFilter,
        leagueId: leagueFilter,
      };
      const res = await contestServices.getAllFantasyGames(payload);
      const result = res?.data?.data?.result || [];
      const total = res?.data?.data?.totalRecords || 0;
      setGames(result);
      setTotalRecords(total);
      setTotalPages(Math.ceil(total / pageSize));
    } catch (err) {
      console.error("Failed to fetch fantasy games", err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search function
  const debouncedSearchFunction = useCallback(
    debounce((query) => {
      setDebouncedSearch(query);
      updateSearchParams(searchParams, setSearchParams, query, 1);
    }, 400),
    [searchParams, setSearchParams]
  );

  // Handle search input change
  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    setPageNumber(1);
    debouncedSearchFunction(query);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPageNumber(page);
    updatePageParams(searchParams, setSearchParams, page);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    setPageSize(newPageSize);
    setPageNumber(1);
    updatePageParams(searchParams, setSearchParams, 1);
  };

  // Handle game type filter change
  const handleGameTypeChange = (event) => {
    setGameTypeFilter(event.target.value);
    setPageNumber(1);
  };

  // Handle league filter change
  const handleLeagueChange = (event) => {
    setLeagueFilter(event.target.value);
    setPageNumber(1);
  };

  // Clear search
  const clearSearch = () => {
    setSearchItem("");
    setDebouncedSearch("");
    setPageNumber(1);
    updateSearchParams(searchParams, setSearchParams, "", 1);
  };

  // Initialize from URL parameters
  useEffect(() => {
    const pageFromUrl = searchParams.get('page');
    const searchFromUrl = searchParams.get('search');
    const pageSizeFromUrl = searchParams.get('pageSize');
    
    if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
      setPageNumber(parseInt(pageFromUrl));
    }
    
    if (searchFromUrl) {
      setSearchItem(searchFromUrl);
      setDebouncedSearch(searchFromUrl);
    }
    
    if (pageSizeFromUrl && !isNaN(pageSizeFromUrl) && parseInt(pageSizeFromUrl) > 0) {
      setPageSize(parseInt(pageSizeFromUrl));
    }
  }, [searchParams]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchFantasyGames();
  }, [contestId, pageNumber, pageSize, debouncedSearch, gameTypeFilter, leagueFilter]);

  return (
    <Box>
      <Helmet>
        <title>Contest Joined User</title>
      </Helmet>

      <CardLayout>
        <CardHeader title="Contest Joined User" />
        <CardContent>
          {/* Search and Filter Section */}
          <Box mb={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              {/* Search Input */}
              <TextField
                placeholder="Search by username, email, or team name..."
                value={searchItem}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchItem && (
                    <InputAdornment position="end">
                      <IconButton onClick={clearSearch} size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
                size="small"
              />

              {/* Game Type Filter */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Game Type</InputLabel>
                <Select
                  value={gameTypeFilter}
                  onChange={handleGameTypeChange}
                  label="Game Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                </Select>
              </FormControl>

              {/* Page Size Selector */}
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  label="Per Page"
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>             
            </Stack>
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center" mt={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                        <TableCell>Username</TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Team Name</strong></TableCell>
                      <TableCell><strong>Contest Name</strong></TableCell>
                      <TableCell><strong>League Name</strong></TableCell>
                      <TableCell><strong>Game Type</strong></TableCell>
                      <TableCell><strong>Remaining Budget</strong></TableCell>
                      <TableCell><strong>Created At</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {games.length > 0 ? (
                      games.map((item) => (
                        <TableRow key={item._id}>
                          <TableCell>{capitalizeFirstLetter(item.userId?.username) || "NA"}</TableCell>
                          <TableCell>{item.userId?.email || "NA"}</TableCell>
                          <TableCell>{item.teamId?.name || "NA"}</TableCell>
                          <TableCell>{item.contestId?.contestName || "NA"}</TableCell>
                          <TableCell>{item.leagueId?.name}</TableCell>
                          <TableCell>{item.gameType}</TableCell>
                          <TableCell>{item.budgetUsed ? formatPrice(item.budgetUsed) : "0"}</TableCell>
                          <TableCell>{moment(item.createdAt).format("DD/MM/YYYY hh:mm A")}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No data found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Section */}
              {totalPages > 1 && (
                <Box mt={3}>
                  <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Showing {((pageNumber - 1) * pageSize) + 1} to {Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords} entries
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item>
                      <Pagination
                        count={totalPages}
                        page={pageNumber}
                        onChange={(_, value) => handlePageChange(value)}
                        color="primary"
                        showFirstButton
                        showLastButton
                        size="medium"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default FantasyGames;
